import React from 'react';
import { Config } from '~/api/def/endpoints/dashboardEndpoint';
import { FundListDashboard } from '~/components/ui/tables/ag/fundListDashboard/FundListDashboard';
import { AIRecommendationsWidgetWrapper } from '~/components/ui/widgets/AIRecommendationsWidget/AIRecommendationsWidgetWrapper';
import { AllocationWidgetWrapper } from '~/components/ui/widgets/AllocationWidget/AllocationWidgetWrapper';
import { CorrelationClusterWidgetWrapper } from '~/components/ui/widgets/CorrelationClusterWidget/CorrelationClusterWidgetWrapper';
import { DynamicHistogramWidgetWrapper } from '~/components/ui/widgets/DynamicHistogramWidgetWrapper';
import { DynamicPieChartWidgetWrapper } from '~/components/ui/widgets/DynamicPieChartWidgetWrapper';
import { DynamicTableWidgetWrapper } from '~/components/ui/widgets/DynamicTableWidgetWrapper/DynamicTableWidgetWrapper';
import { EMSBlotterWidget } from '~/components/ui/widgets/EMSBlotterWidget';
import { ExpectedImpactWidgetWrapper } from '~/components/ui/widgets/ExpectedImpactWidget/ExpectedImpactWidgetWrapper';
import { AssetBreakdownWidgetWrapper } from '~/components/ui/widgets/AssetBreakdownWidget/AssetBreakdownWidgetWrapper';
import { ForwardReturnsWidgetWrapper } from '~/components/ui/widgets/ForwardReturnsWidget/ForwardReturnsWidgetWrapper';
import { HistoricalReturnsWidgetWrapper } from '~/components/ui/widgets/HistoricalReturnsWidget/HistoricalReturnsWidgetWrapper';
import { MarketAnalysisWidgetWrapper } from '~/components/ui/widgets/MarketAnalysisWidget/MarketAnalysisWidgetWrapper';
import { NewsImpactAnalysisWidgetWrapper } from '~/components/ui/widgets/NewsImpactAnalysisWidget/NewsImpactAnalysisWidgetWrapper';
import { PlainTableWidgetWrapper } from '~/components/ui/widgets/PlainTableWidget';
import { ProjectedRiskMetricsWidgetWrapper } from '~/components/ui/widgets/ProjectedRiskMetricsWidget/ProjectedRiskMetricsWidgetWrapper';
import { ProposedPortfolioWidgetWrapper } from '~/components/ui/widgets/ProposedPortfolioWidget/ProposedPortfolioWidgetWrapper';
import { RecommendationReportWidgetWrapper } from '~/components/ui/widgets/RecommendationReportWidget/RecommendationReportWidgetWrapper';
import { RiskAlertsWidgetWrapper } from '~/components/ui/widgets/RiskAlertsWidget/RiskAlertsWidgetWrapper';
import { SectorAllocationWidgetWrapper } from '~/components/ui/widgets/SectorAllocationWidget/SectorAllocationWidgetWrapper';
import AggrGrossIrrLineChart from '~/lib/recharts/aggrGrossIrrLineChart';
import AggrGrossMoicLineChart from '~/lib/recharts/aggrGrossMoicLineChart';
import PCompanyInvestedPieChart from '~/lib/recharts/pCompanyInvestedPieChart';
import PCompanySectorPieChart from '~/lib/recharts/pCompanySectorPieChart';
import { mapBar } from '~/utils/mapBar';

export interface WidgetDefinition {
  /** Pattern to match in widget ID (e.g., 'allocation', 'risk-alerts') */
  idPattern: string;
  /**
   * Priority order for pattern matching (NOT layout positions!)
   * Higher = checked first. Important for overlapping patterns:
   * - 'allocation-table' (priority 100) must come before 'allocation' (priority 50)
   * - Otherwise 'allocation-table-widget-123' would match 'allocation' first
   */
  priority: number;
  /**
   * Default dimensions - FALLBACK ONLY
   * Used when adding widgets programmatically (not in default layout)
   * Actual layout positions come from constants.ts or organizationConfig.ts
   */
  dimensions: { w: number; h: number };
  /** Function to render widget from config (for existing widgets) */
  renderFromConfig: (
    config: Config,
    mapBarFn?: typeof mapBar,
  ) => React.ReactElement | null;
  /** Function to create widget from ID (for new widgets) */
  createFromId: (widgetId: string) => React.ReactElement | null;
}

/**
 * Centralized widget registry
 *
 * ⚠️ IMPORTANT: This registry is for widget RENDERING, not layout positions!
 * - Priority = pattern matching order (NOT x/y positions)
 * - Dimensions = fallback sizes (NOT actual layout positions)
 *
 * Layout positions (x, y, w, h) come from:
 * - src/constants/constants.ts (for default SL layout)
 * - src/config/organizationConfig.ts (for org-specific layouts)
 *
 * To add a new widget:
 * 1. Add entry here with idPattern, priority, dimensions, render functions
 * 2. Add to default layout in constants.ts or organizationConfig.ts with actual x, y, w, h
 */
export const WIDGET_REGISTRY: WidgetDefinition[] = [
  {
    idPattern: 'correlation-cluster',
    priority: 60,
    dimensions: { w: 8, h: 14 },
    renderFromConfig: (config) => (
      <CorrelationClusterWidgetWrapper
        widgetId={config.id}
        key={config.id}
        shortDescription="Correlation Cluster"
      />
    ),
    createFromId: (widgetId) => (
      <CorrelationClusterWidgetWrapper
        widgetId={widgetId}
        key={widgetId}
        shortDescription="Correlation Cluster"
      />
    ),
  },
  {
    idPattern: 'sector-allocation',
    priority: 50,
    dimensions: { w: 8, h: 13 },
    renderFromConfig: (config) => (
      <SectorAllocationWidgetWrapper
        widgetId={config.id}
        key={config.id}
        title="Allocation Analysis"
      />
    ),
    createFromId: (widgetId) => (
      <SectorAllocationWidgetWrapper
        widgetId={widgetId}
        key={widgetId}
        title="Allocation Analysis"
      />
    ),
  },
  {
    idPattern: 'allocation-table-universe',
    priority: 101,
    dimensions: { w: 8, h: 13 },
    renderFromConfig: (config) => (
      <PlainTableWidgetWrapper
        widgetId={config.id}
        key={config.id}
        title="Reference Universe"
        primaryBatchType={config.primaryBatchType || 'universe'}
        comparisonBatchType="proposed"
      />
    ),
    createFromId: (widgetId) => (
      <PlainTableWidgetWrapper
        widgetId={widgetId}
        key={widgetId}
        title="Reference Universe"
        primaryBatchType="universe"
        comparisonBatchType="proposed"
      />
    ),
  },
  {
    idPattern: 'allocation-table',
    priority: 100,
    dimensions: { w: 8, h: 13 },
    renderFromConfig: (config) => (
      <PlainTableWidgetWrapper
        widgetId={config.id}
        key={config.id}
        title="Current Holdings"
      />
    ),
    createFromId: (widgetId) => (
      <PlainTableWidgetWrapper
        widgetId={widgetId}
        key={widgetId}
        title="Current Holdings"
      />
    ),
  },
  {
    idPattern: 'allocation',
    priority: 50,
    dimensions: { w: 6, h: 12 },
    renderFromConfig: (config) => (
      <AllocationWidgetWrapper
        widgetId={config.id}
        key={config.id}
        shortDescription="Reference Universe"
      />
    ),
    createFromId: (widgetId) => (
      <AllocationWidgetWrapper widgetId={widgetId} key={widgetId} />
    ),
  },
  {
    idPattern: 'proposed-portfolio',
    priority: 50,
    dimensions: { w: 6, h: 10 },
    renderFromConfig: (config) => (
      <ProposedPortfolioWidgetWrapper
        widgetId={config.id}
        key={config.id}
        shortDescription="Current VS AI Portfolio"
      />
    ),
    createFromId: (widgetId) => (
      <ProposedPortfolioWidgetWrapper widgetId={widgetId} key={widgetId} />
    ),
  },
  {
    idPattern: 'ai-recommendations',
    priority: 50,
    dimensions: { w: 6, h: 12 },
    renderFromConfig: (config) => (
      <AIRecommendationsWidgetWrapper
        widgetId={config.id}
        key={config.id}
        shortDescription="AI Analysis Results"
      />
    ),
    createFromId: (widgetId) => (
      <AIRecommendationsWidgetWrapper widgetId={widgetId} key={widgetId} />
    ),
  },
  {
    idPattern: 'projected-risk-metrics',
    priority: 50,
    dimensions: { w: 4, h: 7 },
    renderFromConfig: (config) => (
      <ProjectedRiskMetricsWidgetWrapper
        widgetId={config.id}
        key={config.id}
        shortDescription="Projected Risk: Current vs Proposed"
      />
    ),
    createFromId: (widgetId) => (
      <ProjectedRiskMetricsWidgetWrapper widgetId={widgetId} key={widgetId} />
    ),
  },
  {
    idPattern: 'expected-impact',
    priority: 50,
    dimensions: { w: 4, h: 6 },
    renderFromConfig: (config) => (
      <ExpectedImpactWidgetWrapper
        widgetId={config.id}
        key={config.id}
        shortDescription="Expected Impact"
      />
    ),
    createFromId: (widgetId) => (
      <ExpectedImpactWidgetWrapper widgetId={widgetId} key={widgetId} />
    ),
  },
  {
    idPattern: 'risk-alerts',
    priority: 50,
    dimensions: { w: 4, h: 7 },
    renderFromConfig: (config) => (
      <RiskAlertsWidgetWrapper
        widgetId={config.id}
        key={config.id}
        shortDescription="Risk Alerts"
      />
    ),
    createFromId: (widgetId) => (
      <RiskAlertsWidgetWrapper
        widgetId={widgetId}
        key={widgetId}
        shortDescription="Risk Alerts"
      />
    ),
  },
  {
    idPattern: 'market-analysis',
    priority: 50,
    dimensions: { w: 4, h: 8 },
    renderFromConfig: (config) => (
      <MarketAnalysisWidgetWrapper
        widgetId={config.id}
        key={config.id}
        shortDescription="Market Analysis"
      />
    ),
    createFromId: (widgetId) => (
      <MarketAnalysisWidgetWrapper
        widgetId={widgetId}
        key={widgetId}
        shortDescription="Market Analysis"
      />
    ),
  },
  {
    idPattern: 'news-impact-analysis',
    priority: 50,
    dimensions: { w: 4, h: 7 },
    renderFromConfig: (config) => (
      <NewsImpactAnalysisWidgetWrapper
        widgetId={config.id}
        key={config.id}
        shortDescription="News Impact Analysis"
      />
    ),
    createFromId: (widgetId) => (
      <NewsImpactAnalysisWidgetWrapper
        widgetId={widgetId}
        key={widgetId}
        shortDescription="News Impact Analysis"
      />
    ),
  },
  {
    idPattern: 'companies-per-sector',
    priority: 50,
    dimensions: { w: 6, h: 9 },
    renderFromConfig: (config) => (
      <PCompanySectorPieChart widgetId={config.id} key={config.id} />
    ),
    createFromId: (widgetId) => (
      <PCompanySectorPieChart widgetId={widgetId} key={widgetId} />
    ),
  },
  {
    idPattern: 'invested-per-sector',
    priority: 50,
    dimensions: { w: 6, h: 9 },
    renderFromConfig: (config) => (
      <PCompanyInvestedPieChart widgetId={config.id} key={config.id} />
    ),
    createFromId: (widgetId) => (
      <PCompanyInvestedPieChart widgetId={widgetId} key={widgetId} />
    ),
  },
  {
    idPattern: 'aggr-gross-moic',
    priority: 50,
    dimensions: { w: 6, h: 9 },
    renderFromConfig: (config) => (
      <AggrGrossMoicLineChart widgetId={config.id} key={config.id} />
    ),
    createFromId: (widgetId) => (
      <AggrGrossMoicLineChart widgetId={widgetId} key={widgetId} />
    ),
  },
  {
    idPattern: 'aggr-gross-irr',
    priority: 50,
    dimensions: { w: 6, h: 9 },
    renderFromConfig: (config) => (
      <AggrGrossIrrLineChart widgetId={config.id} key={config.id} />
    ),
    createFromId: (widgetId) => (
      <AggrGrossIrrLineChart widgetId={widgetId} key={widgetId} />
    ),
  },
  {
    idPattern: 'ems-blotter',
    priority: 50,
    dimensions: { w: 6, h: 9 },
    renderFromConfig: (config) => (
      <EMSBlotterWidget widgetId={config.id} key={config.id} />
    ),
    createFromId: (widgetId) => (
      <EMSBlotterWidget widgetId={widgetId} key={widgetId} />
    ),
  },
  {
    idPattern: 'historical-returns',
    priority: 50,
    dimensions: { w: 4, h: 10 },
    renderFromConfig: (config) => (
      <HistoricalReturnsWidgetWrapper
        widgetId={config.id}
        key={config.id}
        title="Historical Returns"
      />
    ),
    createFromId: (widgetId) => (
      <HistoricalReturnsWidgetWrapper
        widgetId={widgetId}
        key={widgetId}
        title="Historical Returns"
      />
    ),
  },
  {
    idPattern: 'forward-returns',
    priority: 50,
    dimensions: { w: 4, h: 10 },
    renderFromConfig: (config) => (
      <ForwardReturnsWidgetWrapper
        widgetId={config.id}
        key={config.id}
        title="Forward & Real Returns"
      />
    ),
    createFromId: (widgetId) => (
      <ForwardReturnsWidgetWrapper
        widgetId={widgetId}
        key={widgetId}
        title="Forward & Real Returns"
      />
    ),
  },
  {
    idPattern: 'asset-breakdown-proposed',
    priority: 60,
    dimensions: { w: 6, h: 12 },
    renderFromConfig: (config) => (
      <AssetBreakdownWidgetWrapper
        widgetId={config.id}
        key={config.id}
        title="Forward Returns Breakdown (AI Portfolio)"
        scenario="proposed"
      />
    ),
    createFromId: (widgetId) => (
      <AssetBreakdownWidgetWrapper
        widgetId={widgetId}
        key={widgetId}
        title="Forward Returns Breakdown (AI Portfolio)"
        scenario="proposed"
      />
    ),
  },
  {
    idPattern: 'asset-breakdown-current',
    priority: 60,
    dimensions: { w: 6, h: 12 },
    renderFromConfig: (config) => (
      <AssetBreakdownWidgetWrapper
        widgetId={config.id}
        key={config.id}
        title="Forward Returns Breakdown (Current Portfolio)"
        scenario="current_holdings"
      />
    ),
    createFromId: (widgetId) => (
      <AssetBreakdownWidgetWrapper
        widgetId={widgetId}
        key={widgetId}
        title="Forward Returns Breakdown (Current Portfolio)"
        scenario="current_holdings"
      />
    ),
  },
  {
    idPattern: 'fund-list',
    priority: 50,
    dimensions: { w: 6, h: 9 },
    renderFromConfig: (config) => (
      <FundListDashboard
        widgetId={config.id}
        key={config.id}
        refetchFundListAnalytics={() => void 0}
      />
    ),
    createFromId: (widgetId) => (
      <FundListDashboard
        widgetId={widgetId}
        key={widgetId}
        refetchFundListAnalytics={() => void 0}
      />
    ),
  },
  {
    idPattern: 'recommendation-report',
    priority: 50,
    dimensions: { w: 4, h: 12 },
    renderFromConfig: (config) => (
      <RecommendationReportWidgetWrapper widgetId={config.id} key={config.id} />
    ),
    createFromId: (widgetId) => (
      <RecommendationReportWidgetWrapper widgetId={widgetId} key={widgetId} />
    ),
  },
];

/**
 * Find widget definition by ID pattern
 * Returns the highest priority matching widget
 */
export const findWidgetDefinition = (
  widgetId: string,
): WidgetDefinition | null => {
  // Sort by priority (highest first) to check specific patterns before general ones
  const sortedRegistry = [...WIDGET_REGISTRY].sort(
    (a, b) => b.priority - a.priority,
  );

  return sortedRegistry.find((def) => widgetId.includes(def.idPattern)) || null;
};
